// Small numeric helpers shared by phases and content (24.2: shared helpers live in core).
// Only + - * / and comparisons, so results are identical across JS engines (24.13).

/** Round to the state precision (24.7: numbers are rounded to 1e-6 on write). Never returns -0. */
export function round6(x: number): number {
  const r = Math.round(x * 1e6) / 1e6;
  return r === 0 ? 0 : r;
}

export function clamp(x: number, min: number, max: number): number {
  return x < min ? min : x > max ? max : x;
}

/** Piecewise linear through sorted points, flat outside the range (e.g. f(S) in Section 8). */
export function piecewise(points: readonly (readonly [number, number])[], x: number): number {
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined) return 0;
  if (x <= first[0]) return first[1];
  if (x >= last[0]) return last[1];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1] as readonly [number, number];
    const b = points[i] as readonly [number, number];
    if (x <= b[0]) return a[1] + ((x - a[0]) / (b[0] - a[0])) * (b[1] - a[1]);
  }
  return last[1];
}

/** base^n for a non-negative integer n by repeated multiplication (engine-independent). */
export function powInt(base: number, n: number): number {
  let out = 1;
  for (let i = 0; i < n; i++) out *= base;
  return out;
}

export function sum(values: readonly number[]): number {
  let total = 0;
  for (const v of values) total += v;
  return total;
}

export function mean(values: readonly number[]): number {
  return values.length === 0 ? 0 : sum(values) / values.length;
}

/** Gini coefficient of non-negative values (Section 16). 0 = perfect equality. */
export function gini(values: readonly number[]): number {
  const n = values.length;
  if (n === 0) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  let cumulative = 0;
  let weighted = 0;
  for (let i = 0; i < n; i++) {
    const v = sorted[i] as number;
    cumulative += v;
    weighted += (i + 1) * v;
  }
  if (cumulative === 0) return 0;
  return (2 * weighted) / (n * cumulative) - (n + 1) / n;
}

/** Least-squares slope of values against their index (Section 20 stock trend). */
export function slope(values: readonly number[]): number {
  const n = values.length;
  if (n < 2) return 0;
  const xMean = (n - 1) / 2;
  const yMean = mean(values);
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (i - xMean) * ((values[i] as number) - yMean);
    den += (i - xMean) * (i - xMean);
  }
  return den === 0 ? 0 : num / den;
}

/**
 * Least-squares trend of a seasonal series with one level per season of the year (ordinary
 * least squares with season terms): the slope of values against their index after each
 * season's own mean is taken out, so a harvest peak or a Winter draw-down that repeats every
 * year is not read as a trend (Section 20 stock trend, ADR-0018). Values are grouped by their
 * position modulo `period`; with fewer than two of any one season the slope is 0.
 */
export function seasonalSlope(values: readonly number[], period: number): number {
  const n = values.length;
  if (period <= 1) return slope(values);
  let num = 0;
  let den = 0;
  for (let phase = 0; phase < period; phase++) {
    let count = 0;
    let tSum = 0;
    let ySum = 0;
    for (let i = phase; i < n; i += period) {
      count++;
      tSum += i;
      ySum += values[i] as number;
    }
    if (count < 2) continue;
    const tMean = tSum / count;
    const yMean = ySum / count;
    for (let i = phase; i < n; i += period) {
      num += (i - tMean) * ((values[i] as number) - yMean);
      den += (i - tMean) * (i - tMean);
    }
  }
  return den === 0 ? 0 : num / den;
}
